import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TimeRange } from '@/api/statistics';
import type { StatsPeriodLabels } from '@sharecost/shared';

// Whole accounting periods (user's month start), 'ALL' = no date filter.
// Labels come from the shared groups namespace (same wording as mobile).
const TIME_RANGES: { value: TimeRange; labelKey: string }[] = [
  { value: 'MONTH', labelKey: 'stats.range.month' },
  { value: '30D', labelKey: 'stats.range.last30' },
  { value: '3M', labelKey: 'stats.range.3m' },
  { value: '6M', labelKey: 'stats.range.6m' },
  { value: '1Y', labelKey: 'stats.range.1y' },
  { value: 'ALL', labelKey: 'stats.range.all' },
];

interface GroupOption {
  id: string;
  name: string;
  color: string;
}

interface StatisticsFiltersProps {
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
  /** Period navigator: labels for the browsed period and the offset (0 = current) */
  periodLabels: StatsPeriodLabels;
  periodOffset: number;
  onPeriodOffsetChange: (offset: number) => void;
  groups?: GroupOption[];
  selectedGroupId?: string;
  onGroupChange: (groupId: string | undefined) => void;
}

export function StatisticsFilters({
  timeRange,
  onTimeRangeChange,
  periodLabels,
  periodOffset,
  onPeriodOffsetChange,
  groups,
  selectedGroupId,
  onGroupChange,
}: StatisticsFiltersProps) {
  const { t } = useTranslation(['groups', 'statistics']);
  const showGroupFilter = groups && groups.length > 1;
  // Browsing never goes into the future and is frozen for the unbounded range
  const navigatorDisabled = timeRange === 'ALL';
  const canGoForward = !navigatorDisabled && periodOffset < 0;
  const navButtonClass = (enabled: boolean) =>
    `rounded-md p-2 transition-colors ${
      enabled ? 'text-text-secondary hover:bg-surface-tertiary hover:text-text-primary' : 'text-text-quaternary cursor-default'
    }`;

  return (
    <div className="space-y-3">
      {/* Time range tabs */}
      <div className="flex items-center gap-1 rounded-lg bg-surface-tertiary p-1">
        {TIME_RANGES.map(opt => (
          <button
            key={opt.value}
            onClick={() => onTimeRangeChange(opt.value)}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              timeRange === opt.value
                ? 'bg-surface-primary text-text-primary shadow-sm'
                : 'text-text-tertiary hover:text-text-secondary'
            }`}
          >
            {t(opt.labelKey)}
          </button>
        ))}
      </div>

      {/* Period navigator: previous / current / next */}
      <div className="flex items-center justify-between rounded-lg bg-surface-primary px-3 py-2.5 ring-1 ring-border-primary">
        <button
          type="button"
          onClick={() => onPeriodOffsetChange(periodOffset - 1)}
          disabled={navigatorDisabled}
          aria-label={t('statistics:navigator.previous')}
          className={navButtonClass(!navigatorDisabled)}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onPeriodOffsetChange(0)}
          disabled={navigatorDisabled || periodOffset === 0}
          aria-label={t('statistics:navigator.current')}
          className="flex min-w-0 flex-1 flex-col items-center px-3 py-0.5"
        >
          <span className="truncate text-sm font-semibold text-text-primary">{periodLabels.title}</span>
          {periodLabels.subtitle && (
            <span className="truncate text-xs text-text-tertiary">{periodLabels.subtitle}</span>
          )}
        </button>
        <button
          type="button"
          onClick={() => onPeriodOffsetChange(Math.min(0, periodOffset + 1))}
          disabled={!canGoForward}
          aria-label={t('statistics:navigator.next')}
          className={navButtonClass(canGoForward)}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Group filter chips */}
      {showGroupFilter && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => onGroupChange(undefined)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              !selectedGroupId
                ? 'bg-gray-900 text-white dark:bg-brand-600 dark:text-white'
                : 'bg-surface-tertiary text-text-secondary hover:bg-surface-inset'
            }`}
          >
            {t('expenses:filter.allGroups')}
          </button>
          {groups.map(g => (
            <button
              key={g.id}
              onClick={() => onGroupChange(g.id)}
              className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                selectedGroupId === g.id
                  ? 'bg-gray-900 text-white dark:bg-brand-600 dark:text-white'
                  : 'bg-surface-tertiary text-text-secondary hover:bg-surface-inset'
              }`}
            >
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: g.color }}
              />
              {g.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
